import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileSpreadsheet, RefreshCw, Upload, CheckCircle2, RotateCcw, Download,
  Activity, AlertTriangle, XCircle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const API_URL = process.env.REACT_APP_API_URL;

export function MasterCatalogPanel() {
  const fileRef = useRef(null);
  const [status, setStatus] = useState({ active_revision: null, revisions: [] });
  const [file, setFile] = useState(null);
  const [revision, setRevision] = useState(1);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [localeStatus, setLocaleStatus] = useState({ catalog_revision: null, locales: [] });
  const [translationBusy, setTranslationBusy] = useState(null);
  const [translationErrors, setTranslationErrors] = useState({});
  const [translationReports, setTranslationReports] = useState({});
  const [translationJobs, setTranslationJobs] = useState({});
  const [manualTranslations, setManualTranslations] = useState({});
  const [manualSaving, setManualSaving] = useState('');
  const [translationFile, setTranslationFile] = useState(null);
  const [translationPreview, setTranslationPreview] = useState(null);
  const [translationImportBusy, setTranslationImportBusy] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthBusy, setHealthBusy] = useState(false);
  const [openSector, setOpenSector] = useState(null);

  const loadHealth = async () => {
    setHealthBusy(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/quote/master-catalog/admin/health`,
        { withCredentials: true },
      );
      setHealth(data || null);
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error(err.response?.data?.detail || 'Katalog sağlık raporu yüklenemedi');
      }
      setHealth(null);
    } finally {
      setHealthBusy(false);
    }
  };

  const loadStatus = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/quote/master-catalog/admin/status`,
        { withCredentials: true },
      );
      setStatus(data || { active_revision: null, revisions: [] });
      const active = Number(data?.active_revision || 0);
      setRevision((current) => (current > active ? current : active + 1));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Master katalog durumu yüklenemedi');
    }
  };

  const loadLocaleStatus = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/quote/master-catalog/admin/localizations/status`,
        { withCredentials: true },
      );
      setLocaleStatus(data || { catalog_revision: null, locales: [] });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Katalog dil durumu yüklenemedi';
      setLocaleStatus({ catalog_revision: null, locales: [], error: detail });
      toast.error(detail);
    }
  };

  const loadTranslationJobs = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/quote/master-catalog/admin/localizations/jobs`,
        { withCredentials: true },
      );
      const byLocale = {};
      (data?.jobs || []).forEach((job) => {
        byLocale[job.locale] = job;
      });
      setTranslationJobs(byLocale);
      const running = (data?.jobs || []).find(
        (job) => job.status === 'running' || job.status === 'queued',
      );
      setTranslationBusy(running?.locale || null);

      const reports = {};
      (data?.jobs || []).forEach((job) => {
        if (['complete', 'needs_review', 'error'].includes(job.status)) {
          reports[job.locale] = {
            status: job.status,
            translated: Number(job.translated || 0),
            expected: Number(job.expected || 0),
            missing: Number(job.missing || 0),
            generated: Number(job.generated_total || 0),
            unresolved: job.unresolved || [],
            error: job.error || '',
          };
        }
      });
      setTranslationReports((current) => ({ ...current, ...reports }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) toast.error(detail);
    }
  };

  const refreshAll = async () => {
    await Promise.all([loadStatus(), loadLocaleStatus(), loadTranslationJobs(), loadHealth()]);
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!translationBusy) return undefined;
    const timer = window.setInterval(async () => {
      // Job documents already contain translated/expected/missing/percent.
      // Avoid repeatedly triggering the catalog-wide coverage endpoint.
      await loadTranslationJobs();
    }, 5000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationBusy]);

  const runImport = async (dryRun) => {
    if (!file) {
      toast.error('Önce Master Catalog XLSX dosyasını seçin');
      return;
    }
    if (!Number.isInteger(Number(revision)) || Number(revision) < 1) {
      toast.error('Geçerli bir revision numarası girin');
      return;
    }

    if (!dryRun) {
      const ok = window.confirm(
        `Revision ${revision} yayınlanacak ve aktif master katalog yapılacak. Devam edilsin mi?`,
      );
      if (!ok) return;
    }

    const form = new FormData();
    form.append('file', file);
    form.append('revision', String(revision));
    form.append('dry_run', dryRun ? 'true' : 'false');

    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/quote/master-catalog/admin/import-xlsx`,
        form,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      setPreview(data);
      if (dryRun) {
        toast.success(
          `Doğrulandı: ${data.families} aile / ${data.variants} varyant`,
        );
      } else {
        toast.success(`Master katalog revision ${data.revision} yayınlandı`);
        await loadStatus();
        await loadHealth();
        await loadLocaleStatus();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Master katalog işlemi başarısız');
    } finally {
      setBusy(false);
    }
  };

  const activate = async (targetRevision) => {
    if (targetRevision === status.active_revision) return;
    const ok = window.confirm(
      `Master katalog revision ${targetRevision} yeniden aktif edilsin mi?`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/quote/master-catalog/admin/activate/${targetRevision}`,
        {},
        { withCredentials: true },
      );
      toast.success(`Revision ${targetRevision} aktif edildi`);
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revision aktif edilemedi');
    } finally {
      setBusy(false);
    }
  };

  const cancelLocaleJob = async (locale) => {
    const ok = window.confirm(
      `${locale.toUpperCase()} için çalışan arka plan çeviri işi durdurulsun mu? Mevcut kaydedilmiş çeviriler korunur.`,
    );
    if (!ok) return;

    setTranslationBusy(locale);
    try {
      await axios.post(
        `${API_URL}/api/quote/master-catalog/admin/localizations/jobs/${locale}/cancel`,
        {},
        { withCredentials: true },
      );
      toast.success(`${locale.toUpperCase()} arka plan çeviri işi durduruldu.`);
      await Promise.all([loadLocaleStatus(), loadTranslationJobs()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Çeviri işi durdurulamadı');
    } finally {
      setTranslationBusy(null);
    }
  };

  const generateLocale = async (locale, rebuild = false) => {
    if (rebuild) {
      const ok = window.confirm(
        `${locale.toUpperCase()} kataloğundaki tüm çeviriler baştan üretilecek. İşlem arka planda devam eder; bulunamayanlar rapora yazılır. Devam edilsin mi?`,
      );
      if (!ok) return;
    }

    setTranslationErrors((current) => ({ ...current, [locale]: '' }));
    setTranslationReports((current) => ({ ...current, [locale]: null }));
    setTranslationBusy(locale);

    try {
      const form = new FormData();
      form.append('locale', locale);
      form.append('rebuild', rebuild ? 'true' : 'false');
      await axios.post(
        `${API_URL}/api/quote/master-catalog/admin/localizations/start`,
        form,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      toast.success(
        rebuild
          ? `${locale.toUpperCase()} çevirileri baştan oluşturuluyor. Hatalı satırlar işlemi durdurmayacak.`
          : `${locale.toUpperCase()} çevirisi arka planda başlatıldı. Oturum kapansa da işlem devam eder.`,
      );
      await Promise.all([loadLocaleStatus(), loadTranslationJobs()]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Katalog çevirisi başlatılamadı';
      setTranslationErrors((current) => ({ ...current, [locale]: detail }));
      setTranslationBusy(null);
      toast.error(detail);
    }
  };

  const downloadTranslations = async () => {
    setTranslationImportBusy(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/quote/master-catalog/admin/localizations/export-xlsx`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `paydaai_master_catalog_translations_r${status.active_revision || 'current'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Çeviri Excel dosyası indirildi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Çeviri dosyası indirilemedi');
    } finally {
      setTranslationImportBusy(false);
    }
  };

  const runTranslationImport = async (dryRun) => {
    if (!translationFile) {
      toast.error('Önce düzeltilmiş çeviri XLSX dosyasını seçin');
      return;
    }

    if (!dryRun) {
      const counts = translationPreview?.change_counts || {};
      const total = Number(translationPreview?.change_total || 0);
      const ok = window.confirm(
        `${total} çeviri değişikliği aktif kataloğa uygulanacak. `
        + `EN: ${counts.en || 0}, DE: ${counts.de || 0}, FR: ${counts.fr || 0}, ES: ${counts.es || 0}. Devam edilsin mi?`,
      );
      if (!ok) return;
    }

    const form = new FormData();
    form.append('file', translationFile);
    form.append('dry_run', dryRun ? 'true' : 'false');

    setTranslationImportBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/quote/master-catalog/admin/localizations/import-xlsx`,
        form,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      setTranslationPreview(data);
      if (dryRun) {
        if (Number(data?.rejected_count || 0) > 0) {
          toast.error(`Dosyada ${data.rejected_count} kalite hatası bulundu; uygulanmadı.`);
        } else {
          toast.success(`${data.change_total || 0} değişiklik doğrulandı. Henüz veritabanına yazılmadı.`);
        }
      } else {
        toast.success(`${data.applied || 0} çeviri değişikliği uygulandı.`);
        await Promise.all([loadLocaleStatus(), loadTranslationJobs()]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Çeviri XLSX işlemi başarısız');
    } finally {
      setTranslationImportBusy(false);
    }
  };

  const saveManualTranslation = async (locale, item) => {
    const key = `${locale}:${item.translation_key}`;
    const value = (manualTranslations[key] || '').trim();
    if (!value) {
      toast.error('Önce çeviri değerini girin');
      return;
    }

    setManualSaving(key);
    try {
      const form = new FormData();
      form.append('locale', locale);
      form.append('translation_key', item.translation_key);
      form.append('value', value);
      await axios.post(
        `${API_URL}/api/quote/master-catalog/admin/localizations/manual`,
        form,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      setManualTranslations((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      toast.success('Çeviri kaydedildi');
      await Promise.all([loadLocaleStatus(), loadTranslationJobs()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Çeviri kaydedilemedi');
    } finally {
      setManualSaving('');
    }
  };

  const sectorEntries = preview?.sectors ? Object.entries(preview.sectors) : [];
  const localeRows = (localeStatus.locales || []).filter((row) => row.locale !== 'tr');

  return (
    <div data-testid="master-catalog-panel">
      <div className="flex flex-col gap-2 mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-[#1A1A2E]">
              Master Katalog
            </h1>
            <p className="text-sm text-[#595959] mt-1">
              PaydaAI genel ürün taksonomisini XLSX revision olarak doğrula, yayınla veya geri al.
            </p>
          </div>
          <Button variant="outline" onClick={refreshAll} disabled={busy}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Yenile
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-6">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              XLSX Revision Yayınla
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-4">
              <div>
                <Label>Master Catalog XLSX</Label>
                <Input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="mt-2"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] || null);
                    setPreview(null);
                  }}
                  data-testid="master-catalog-file"
                />
                <p className="text-xs text-[#737373] mt-2">
                  Dosyada 02_Family_Master sayfası zorunludur; 04_Field_Dictionary varsa teknik alan tipleri de alınır.
                </p>
              </div>
              <div>
                <Label>Revision</Label>
                <Input
                  className="mt-2"
                  type="number"
                  min="1"
                  value={revision}
                  onChange={(e) => {
                    setRevision(Number(e.target.value));
                    setPreview(null);
                  }}
                  data-testid="master-catalog-revision"
                />
              </div>
            </div>

            <div className="flex gap-3 flex-wrap mt-5">
              <Button
                variant="outline"
                disabled={busy || !file}
                onClick={() => runImport(true)}
                data-testid="master-catalog-validate"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Önce Doğrula
              </Button>
              <Button
                disabled={busy || !file || !preview || preview.status !== 'validated'
                  || Number(preview.revision) !== Number(revision)}
                onClick={() => runImport(false)}
                data-testid="master-catalog-publish"
              >
                <Upload className="w-4 h-4 mr-2" />
                Yayınla ve Aktif Et
              </Button>
            </div>

            {preview && (
              <div className="mt-5 rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] p-4">
                <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
                  <span><strong>Durum:</strong> {preview.status}</span>
                  <span><strong>Revision:</strong> {preview.revision}</span>
                  <span><strong>Aile:</strong> {preview.families}</span>
                  <span><strong>Varyant:</strong> {preview.variants}</span>
                </div>
                {sectorEntries.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
                    {sectorEntries.map(([key, count]) => (
                      <div key={key} className="bg-white border rounded-lg px-3 py-2 text-sm">
                        <span className="font-medium">{key}</span>
                        <span className="float-right">{count} aile</span>
                      </div>
                    ))}
                  </div>
                )}
                {Number(preview.spec_warning_count || 0) > 0 && (
                  <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
                    <p className="font-medium text-amber-900 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {preview.spec_warning_count} varyantta zorunlu teknik değer eksik
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                      Bu varyantları kural motoru kullanamaz. Excel&apos;de ilgili aileye
                      {' '}<code>variant_specs</code> sütunu ekleyin
                      (örnek: <code>channels=16; hdd_bays=2 | channels=32</code>)
                      veya varyant adına birimi yazın (örn. &quot;16 Kanal&quot;).
                    </p>
                    <ul className="mt-2 max-h-40 overflow-auto text-xs text-amber-900 space-y-0.5">
                      {(preview.spec_warnings || []).map((w) => (
                        <li key={w.variant_id}>
                          <span className="font-mono">{w.family_id}</span> — {w.variant}
                          {' '}(eksik: {w.missing.join(', ')})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-xs text-[#737373] mt-3 break-all">
                  SHA-256: {preview.source_digest}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Aktif Revision</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-5xl font-bold text-[#1A1A2E]">
              {status.active_revision ?? '—'}
            </p>
            <p className="text-sm text-[#737373] mt-2">
              Teklif ve katalog API'leri yalnız bu immutable revision'ı okur.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6" data-testid="master-catalog-health">
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-3 flex-wrap">
            <span className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Katalog Sağlığı
            </span>
            <Button variant="outline" size="sm" onClick={loadHealth} disabled={healthBusy}>
              <RefreshCw className={`w-4 h-4 mr-2 ${healthBusy ? 'animate-spin' : ''}`} />
              Kontrol Et
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#595959] mb-4">
            Aktif revision her sektör için AI teklif taslağına uygun mu: teknik değeri eksik
            varyantlar, eksik temel kalemler ve kamera kural motorunun örnek işle denenmesi.
          </p>
          {!health && (
            <p className="text-sm text-[#737373]">
              {healthBusy ? 'Kontrol ediliyor…' : 'Aktif revision yok veya rapor henüz alınmadı.'}
            </p>
          )}
          {health && (
            <div className="space-y-2">
              {(health.sectors || []).map((row) => {
                const Icon = row.status === 'ok' ? CheckCircle2
                  : row.status === 'warning' ? AlertTriangle : XCircle;
                const color = row.status === 'ok' ? 'text-green-600'
                  : row.status === 'warning' ? 'text-amber-600' : 'text-red-600';
                const open = openSector === row.sector_key;
                return (
                  <div key={row.sector_key} className="border rounded-lg">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left"
                      onClick={() => setOpenSector(open ? null : row.sector_key)}
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <Icon className={`w-4 h-4 ${color}`} />
                        {row.sector_key}
                      </span>
                      <span className="text-xs text-[#737373]">
                        {row.total} kalem · {row.issues.length} bulgu
                      </span>
                    </button>
                    {open && (
                      <div className="px-3 pb-3 text-sm space-y-2">
                        {row.issues.length === 0 && (
                          <p className="text-green-700">Sorun bulunmadı.</p>
                        )}
                        {row.issues.map((item) => (
                          <p
                            key={`${item.code}-${item.role || ''}`}
                            className={item.level === 'error' ? 'text-red-700' : 'text-amber-800'}
                          >
                            • {item.message}
                          </p>
                        ))}
                        {row.missing_specs.length > 0 && (
                          <ul className="max-h-40 overflow-auto text-xs text-[#595959] space-y-0.5">
                            {row.missing_specs.map((m) => (
                              <li key={m.id}>
                                {m.name} — eksik: {m.missing.join(', ')}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Çeviri Excel Kontrolü</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#595959] mb-4">
            Aktif katalogdaki Türkçe, İngilizce, Almanca, Fransızca ve İspanyolca
            etiketleri tek Excel dosyasına indir. Dosyayı dışarıda kontrol edip
            düzelttikten sonra önce doğrula; onay vermeden hiçbir değişiklik uygulanmaz.
          </p>

          <div className="flex flex-wrap gap-3 items-center">
            <Button
              variant="outline"
              disabled={translationImportBusy || !status.active_revision}
              onClick={downloadTranslations}
            >
              <Download className="w-4 h-4 mr-2" />
              Çevirileri XLSX İndir
            </Button>

            <Input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="max-w-md"
              disabled={translationImportBusy}
              onChange={(e) => {
                setTranslationFile(e.target.files?.[0] || null);
                setTranslationPreview(null);
              }}
              data-testid="master-catalog-translation-file"
            />

            <Button
              variant="outline"
              disabled={translationImportBusy || !translationFile}
              onClick={() => runTranslationImport(true)}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Dosyayı Doğrula
            </Button>

            <Button
              disabled={
                translationImportBusy
                || !translationFile
                || !translationPreview
                || translationPreview.status !== 'validated'
                || Number(translationPreview.rejected_count || 0) > 0
              }
              onClick={() => runTranslationImport(false)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Onayla ve Uygula
            </Button>
          </div>

          {translationPreview && (
            <div className="mt-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] p-4">
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <span><strong>Revision:</strong> {translationPreview.catalog_revision}</span>
                <span><strong>Satır:</strong> {translationPreview.rows}</span>
                <span><strong>Toplam değişiklik:</strong> {translationPreview.change_total || 0}</span>
                <span><strong>EN:</strong> {translationPreview.change_counts?.en || 0}</span>
                <span><strong>DE:</strong> {translationPreview.change_counts?.de || 0}</span>
                <span><strong>FR:</strong> {translationPreview.change_counts?.fr || 0}</span>
                <span><strong>ES:</strong> {translationPreview.change_counts?.es || 0}</span>
              </div>

              {Number(translationPreview.rejected_count || 0) > 0 && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <div className="font-medium">
                    {translationPreview.rejected_count} değişiklik kalite kontrolünden geçmedi.
                  </div>
                  <div className="mt-2 max-h-48 overflow-auto space-y-1 text-xs">
                    {(translationPreview.rejected || []).map((item) => (
                      <div key={`${item.locale}:${item.translation_key}`}>
                        <strong>{item.locale?.toUpperCase()}</strong> · {item.source_tr}
                        {' → '}{item.new_value || '(boş)'} · {item.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Number(translationPreview.change_total || 0) === 0
                && Number(translationPreview.rejected_count || 0) === 0 && (
                <div className="mt-3 text-sm text-emerald-700">
                  Dosya geçerli; mevcut çevirilerle fark bulunmadı.
                </div>
              )}

              {(translationPreview.changes || []).length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm font-medium">
                    Değişiklik örneklerini göster ({translationPreview.change_total})
                  </summary>
                  <div className="mt-2 max-h-56 overflow-auto space-y-2">
                    {(translationPreview.changes || []).map((item) => (
                      <div
                        key={`${item.locale}:${item.translation_key}`}
                        className="rounded border bg-white p-2 text-xs"
                      >
                        <div className="font-medium">
                          {item.locale?.toUpperCase()} · {item.source_tr}
                        </div>
                        <div className="text-slate-500 break-all">{item.translation_key}</div>
                        <div className="mt-1 line-through text-slate-500">{item.old_value || '(boş)'}</div>
                        <div className="text-emerald-700">{item.new_value}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Katalog Dilleri</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#595959] mb-4">
            Çeviriler ürün ID'lerine bağlı olarak üretilir. Bir dil %100 tamamlanmadan
            katalogda aktif kullanılmaz; yarı Türkçe / yarı yabancı etiket gösterilmez.
          </p>
          {localeStatus.error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {localeStatus.error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {localeRows.map((row) => {
              const job = translationJobs[row.locale];
              const report = translationReports[row.locale];
              const running = job?.status === 'running' || job?.status === 'queued';
              return (
              <div key={row.locale} className="rounded-xl border border-[#E5E5E5] p-4">
                <div className="flex items-center justify-between gap-2">
                  <strong className="text-lg uppercase">{row.locale}</strong>
                  <span className={row.ready ? 'text-emerald-700 text-sm' : 'text-amber-700 text-sm'}>
                    {row.ready ? 'Hazır' : `%${row.percent || 0}`}
                  </span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full mt-3 overflow-hidden">
                  <div
                    className="h-full bg-blue-600"
                    style={{ width: `${Math.min(100, Number(row.percent || 0))}%` }}
                  />
                </div>
                <p className="text-xs text-[#737373] mt-2">
                  {row.translated || 0} / {row.expected || 0} etiket
                </p>
                {translationErrors[row.locale] && (
                  <p className="mt-2 text-xs text-red-700">
                    {translationErrors[row.locale]}
                  </p>
                )}
                {running && (
                  <div className="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700">
                    <div>
                      Arka planda devam ediyor: {Number(job?.translated || row.translated || 0)} / {Number(job?.expected || row.expected || 0)}
                    </div>
                    <Button
                      className="mt-2 h-7 px-2 text-xs"
                      size="sm"
                      variant="outline"
                      onClick={() => cancelLocaleJob(row.locale)}
                    >
                      Arka Plan İşini Durdur
                    </Button>
                  </div>
                )}
                {report && (
                  <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">
                    <div>
                      Son rapor: {report.translated} / {report.expected} etiket tamamlandı.
                    </div>
                    <div>
                      Bu çalışmada {report.generated} yeni etiket kaydedildi.
                    </div>
                    {report.status === 'complete' && (
                      <div className="mt-1 text-emerald-700">Dil grubu tamamen hazır.</div>
                    )}
                    {report.missing > 0 && (
                      <div className="mt-1 text-amber-700">
                        Çevrilemeyen: {report.missing}
                      </div>
                    )}
                    {report.error && (
                      <div className="mt-1 text-red-700">{report.error}</div>
                    )}
                    {(report.unresolved || []).length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer font-medium">
                          Çevrilemeyenleri göster ({report.unresolved.length})
                        </summary>
                        <div className="mt-2 max-h-56 overflow-auto space-y-2">
                          {report.unresolved.map((item) => (
                            <div key={item.translation_key} className="rounded border bg-white p-2">
                              <div className="font-medium">{item.source_tr}</div>
                              <div className="text-[11px] break-all text-slate-500">
                                {item.translation_key}
                              </div>
                              <div className="text-[11px] text-red-700">
                                {item.reason}
                              </div>
                              <div className="mt-2 flex gap-2">
                                <Input
                                  className="h-8 text-xs"
                                  placeholder="Doğru çeviriyi yazın"
                                  value={manualTranslations[`${row.locale}:${item.translation_key}`] || ''}
                                  onChange={(e) => setManualTranslations((current) => ({
                                    ...current,
                                    [`${row.locale}:${item.translation_key}`]: e.target.value,
                                  }))}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={manualSaving === `${row.locale}:${item.translation_key}`}
                                  onClick={() => saveManualTranslation(row.locale, item)}
                                >
                                  {manualSaving === `${row.locale}:${item.translation_key}` ? 'Kaydediliyor...' : 'Kaydet'}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
                <div className="mt-3 grid grid-cols-1 gap-2">
                  <Button
                    className="w-full"
                    variant={row.ready ? 'outline' : 'default'}
                    disabled={translationBusy !== null || row.ready}
                    onClick={() => generateLocale(row.locale, false)}
                  >
                    {running ? 'Arka planda çevriliyor...' : (row.ready ? 'Tamamlandı' : 'Eksikleri Otomatik Tamamla')}
                  </Button>
                  <Button
                    className="w-full"
                    variant="outline"
                    disabled={translationBusy !== null}
                    onClick={() => generateLocale(row.locale, true)}
                  >
                    Baştan Oluştur
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Yayın Geçmişi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Revision</TableHead>
                  <TableHead>Aile</TableHead>
                  <TableHead>Varyant</TableHead>
                  <TableHead>Kaynak</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(status.revisions || []).map((row) => {
                  const active = Number(row.revision) === Number(status.active_revision);
                  return (
                    <TableRow key={row.revision}>
                      <TableCell className="font-semibold">{row.revision}</TableCell>
                      <TableCell>{row.family_count}</TableCell>
                      <TableCell>{row.variant_count}</TableCell>
                      <TableCell className="max-w-[260px] truncate" title={row.source_name}>
                        {row.source_name || '—'}
                      </TableCell>
                      <TableCell>{active ? 'Aktif' : row.status}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy || active}
                          onClick={() => activate(row.revision)}
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Aktif Et
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!(status.revisions || []).length && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-[#737373] py-8">
                      Henüz yayınlanmış master katalog revision'ı yok.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
